import React, { FC, useState } from "react";
import { renderTrackingNumber } from "v3/components/Tracking/helpers";
import {
  StatusLabels,
  SubmittedOrderQuery,
} from "v3/services/graphql/generated";
import { CaretPointDirections } from "@pepdirect/v3/ui/icons";
import {
  StyledButton,
  StyledCaretIconSvg,
  StyledContent,
  StyledFulfillmentItem,
  StyledInfo,
  StyledInfoDetail,
  StyledInfoDetailTitle,
  StyledStatusText,
  StyledTitle,
} from "./item.styled";
import { FulfillmentItemProduct } from "./Product";

const { Canceled, Complete, NoChange, Unknown } = StatusLabels;

export interface FullfillmentItemProps {
  fulfillment: NonNullable<
    NonNullable<SubmittedOrderQuery["submittedOrder"]>["fulfillments"]
  >[number];
}

export const FulfillmentItem: FC<FullfillmentItemProps> = ({ fulfillment }) => {
  const [visible, setVisible] = useState(false);
  const toggleVisibility = () => setVisible(!visible);

  const mappedItems = fulfillment.items.filter(({ item }) => item);

  const notMappedAmount =
    fulfillment.items
      .filter(({ item }) => !item)
      .map(({ quantity }) => quantity)
      .reduce((acc, curr) => (acc || 0) + (curr || 0), 0) || 0;

  const trackingNumber = fulfillment.tracking?.trackingNumber || "";
  const trackingUrl = fulfillment?.tracking?.trackingUrl || "";
  const status = fulfillment?.tracking?.status || "";

  if (fulfillment.status === NoChange || !fulfillment.tracking) {
    return null;
  }

  const renderStatusContainer = () => {
    switch (fulfillment.status) {
      case Complete:
        return (
          <StyledInfo>
            <StyledInfoDetail $flexBasis="30%">
              <StyledInfoDetailTitle>Status:</StyledInfoDetailTitle>
              <StyledStatusText>{status.replace(/_/g, " ")}</StyledStatusText>
            </StyledInfoDetail>
            <StyledInfoDetail $flexBasis="70%">
              <StyledInfoDetailTitle>Tracking:</StyledInfoDetailTitle>
              {renderTrackingNumber(trackingNumber, trackingUrl || "")}
            </StyledInfoDetail>
          </StyledInfo>
        );
      case Canceled:
        return (
          <StyledInfo>
            <StyledInfoDetail $flexBasis="30%">
              <StyledInfoDetailTitle>Status:</StyledInfoDetailTitle>
              <StyledStatusText $cancelled>Canceled</StyledStatusText>
            </StyledInfoDetail>
          </StyledInfo>
        );
      case Unknown:
        return <div />;
    }
  };

  return (
    <StyledFulfillmentItem>
      <StyledTitle>
        {renderStatusContainer()}
        <StyledButton
          onClick={toggleVisibility}
          role="button"
          tabIndex={0}
          aria-label="Show/hide fulfillment details"
        >
          <StyledCaretIconSvg
            height="24px"
            width="24px"
            pointDirection={
              visible ? CaretPointDirections.up : CaretPointDirections.down
            }
          />
        </StyledButton>
      </StyledTitle>
      <StyledContent $visible={visible}>
        {mappedItems.map((item) => (
          <FulfillmentItemProduct item={item} key={item.item?.id} />
        ))}
        {notMappedAmount > 0 && (
          <span>
            {mappedItems.length > 0
              ? `and ${notMappedAmount} more piece`
              : `${notMappedAmount} piece`}
            {notMappedAmount > 1 && notMappedAmount !== 0 ? "s" : ""}
          </span>
        )}
      </StyledContent>
    </StyledFulfillmentItem>
  );
};
